import React, { useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useContent } from '../context/ContentContext';
import { resolveImg } from '../api';
import { socials } from '../data/mock';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

const GrillzSection = () => {
  const { t, lang } = useLang();
  const { content } = useContent();
  const grillz = content?.grillz || [];
  const [active, setActive] = useState(0);

  if (grillz.length === 0) return null;

  const safeActive = active % grillz.length;
  const current = grillz[safeActive];

  const next = () => setActive((i) => (i + 1) % grillz.length);
  const prev = () => setActive((i) => (i - 1 + grillz.length) % grillz.length);

  const openBooking = () => window.open(socials.instagram, '_blank', 'noopener,noreferrer');

  return (
    <section id="grillz" className="section-pad bg-[#0E0E0E] text-[#FAF8F4] relative overflow-hidden">
      {/* Decorative chrome shine */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-[#D4D4D4]/20 to-transparent blur-3xl" />
      </div>

      <div className="container-tight relative">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <span className="eyebrow" style={{ color: '#D4B987' }}>{t.grillz.eyebrow}</span>
          <h2 className="h-display mt-6 text-[#FAF8F4]" style={{ fontSize: 'clamp(48px, 7vw, 88px)' }}>
            {t.grillz.title}
          </h2>
          <p className="text-[15px] text-[#C9C9C9] leading-[1.8] mt-6">{t.grillz.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Featured large image */}
          <div className="lg:col-span-7 relative">
            <div className="aspect-square bg-[#1A1A1A] image-hover ring-1 ring-[#D4B987]/20">
              <img
                src={resolveImg(current.img)}
                alt={lang === 'fr' ? current.nameFr : current.nameEn}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute top-4 left-4 bg-black/80 backdrop-blur px-4 py-2 border border-[#D4B987]/40">
              <div className="text-[9px] tracking-[0.32em] uppercase text-[#D4B987]">
                {String(safeActive + 1).padStart(2, '0')} / {String(grillz.length).padStart(2, '0')}
              </div>
            </div>
          </div>

          {/* Right: details + thumbnails */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles size={14} className="text-[#D4B987]" />
              <span className="text-[10px] tracking-[0.32em] uppercase text-[#D4B987]">
                {current.material || 'Cobalt Chrome'}
              </span>
            </div>

            <h3 className="h-display text-[44px] leading-[1.1] mb-4 text-[#FAF8F4]">
              {lang === 'fr' ? current.nameFr : current.nameEn || current.nameFr}
            </h3>

            <div className="my-6 h-px w-24 bg-gradient-to-r from-[#D4B987] to-transparent" />

            <p className="text-[14px] text-[#C9C9C9] leading-[1.8] mb-8">
              {lang === 'fr'
                ? 'Création unique réalisée sur empreinte dentaire, façonnée à la main dans notre atelier.'
                : 'Unique creation made from your dental impression, handcrafted in our studio.'}
            </p>

            <div className="flex items-center gap-3 mb-8">
              <button
                onClick={prev}
                className="w-11 h-11 border border-[#FAF8F4]/40 flex items-center justify-center hover:bg-[#FAF8F4] hover:text-[#0E0E0E] hover:border-[#FAF8F4] transition-colors"
                aria-label="prev"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={next}
                className="w-11 h-11 border border-[#FAF8F4]/40 flex items-center justify-center hover:bg-[#FAF8F4] hover:text-[#0E0E0E] hover:border-[#FAF8F4] transition-colors"
                aria-label="next"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={openBooking}
                className="ml-auto px-6 py-3 bg-[#D4B987] text-[#0E0E0E] text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#FAF8F4] transition-colors"
              >
                {t.grillz.bookBtn}
              </button>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {grillz.map((g, i) => (
                <button
                  key={g.id}
                  onClick={() => setActive(i)}
                  className={`aspect-square overflow-hidden border-2 transition-all duration-300 ${
                    i === safeActive ? 'border-[#D4B987]' : 'border-transparent opacity-50 hover:opacity-100'
                  }`}
                  aria-label={`grillz ${i + 1}`}
                >
                  <img src={resolveImg(g.img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Global price banner */}
        <div className="mt-20 max-w-2xl mx-auto text-center border-t border-[#D4B987]/30 pt-12">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#8A8A8A]">{t.grillz.priceLabel}</div>
          <div className="display text-[64px] md:text-[80px] leading-none text-[#D4B987] mt-3">90€</div>
          <p className="text-[12px] text-[#8A8A8A] mt-4 italic tracking-wide">
            {lang === 'fr'
              ? '— Tarif variable selon le modèle choisi —'
              : '— Price varies depending on the chosen model —'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default GrillzSection;
